import React, { useState, useRef, useEffect } from 'react';
import { Send, AlertCircle, X, Image, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import MessageList from './MessageList';
import StreamingMessage from './StreamingMessage';
import { ChatSession as ChatSessionType, Message, ImageAttachment } from '@/types/chat';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { API_CONFIG } from '@/config/api';

interface ChatSessionProps {
  session: ChatSessionType;
  sessionId: string;
  onUpdateSession: (updates: Partial<ChatSessionType>) => void;
  onStreamingChange?: (isStreaming: boolean) => void;
}

const ChatSession = ({ session, sessionId, onUpdateSession, onStreamingChange }: ChatSessionProps) => {
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Use a ref to track the latest session state to avoid closure issues
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // File handling utilities
  const MAX_FILE_SIZE_MB = 5;
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are supported.';
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size must be less than ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
  };

  const calculateTotalSize = (currentAttachments: ImageAttachment[], newFiles?: File[]): number => {
    const currentSize = currentAttachments.reduce((total, attachment) => {
      // Rough estimate: base64 is ~4/3 the size of original
      const base64Data = attachment.data_url.split(',')[1];
      return total + (base64Data.length * 0.75);
    }, 0);

    const newSize = newFiles ? newFiles.reduce((total, file) => total + file.size, 0) : 0;
    return currentSize + newSize;
  };

  const handleFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => ACCEPTED_IMAGE_TYPES.includes(file.type));

    if (imageFiles.length === 0) {
      return;
    }

    // Check total size limit
    const totalSize = calculateTotalSize(attachments, imageFiles);
    if (totalSize > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`Total size of all images must be less than ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    // Validate and convert files
    const newAttachments: ImageAttachment[] = [];
    for (const file of imageFiles) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        continue;
      }

      try {
        const dataUrl = await convertFileToBase64(file);
        newAttachments.push({ data_url: dataUrl });
      } catch (error) {
        console.error('Error converting file to base64:', error);
        alert('Error processing image file.');
      }
    }

    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the container, not child elements
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await handleFiles(files);
    }
  };
  
  const streamingChat = useStreamingChat(sessionId, {
    onComplete: (response: string, sources: string[]) => {
      
      // Process the completed response and add bot message (without tool calls)
      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        type: 'bot',
        content: response,
        sources,
        // Note: we don't include toolCalls here as requested - they're only shown during streaming
        timestamp: new Date(),
      };


      // Update messages with the bot response using the ref to get latest state
      const newMessages = [...sessionRef.current.messages, botMessage];
      onUpdateSession({
        messages: newMessages,
        lastMessage: new Date(),
      });

    },
    onError: (error: Error) => {
      console.error('❌ Streaming chat error:', error);
    }
  });

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [session.messages, streamingChat.isStreaming, streamingChat.currentResponse]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  const handleSendMessage = async () => {

    if (!inputValue.trim() || streamingChat.isStreaming) {
      return;
    }

    const userMessage = inputValue.trim();
    const messageAttachments = attachments.length > 0 ? [...attachments] : undefined;
    setInputValue('');
    setAttachments([]); // Clear attachments after sending

    // Add user message optimistically
    const optimisticMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
      attachments: messageAttachments,
    };
    
    const newMessages = [...session.messages, optimisticMessage];
    
    onUpdateSession({
      messages: newMessages,
      lastMessage: new Date(),
    });

    try {
      // Notify that streaming is starting
      onStreamingChange?.(true);
      
      // Send message via streaming
      await streamingChat.sendMessage(userMessage, messageAttachments);
      
      // Notify that streaming has completed
      onStreamingChange?.(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Clear streaming flag on error
      onStreamingChange?.(false);
      
      // Remove optimistic message on error
      onUpdateSession({
        messages: session.messages.filter(m => m.id !== optimisticMessage.id),
      });
    }
  };

  const handleFeedback = (messageId: string, feedback: 'thumbs_up' | 'thumbs_down') => {
    const updatedMessages = session.messages.map(msg => 
      msg.id === messageId 
        ? { ...msg, feedback: msg.feedback === feedback ? null : feedback }
        : msg
    );
    onUpdateSession({ messages: updatedMessages });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-col h-full bg-background relative ${isDragOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-blue-50 bg-opacity-90">
          <div className="text-center">
            <Image className="mx-auto h-12 w-12 text-blue-500 mb-2" />
            <p className="text-lg font-medium text-blue-700">Drop images here</p>
            <p className="text-sm text-blue-600">Max {MAX_FILE_SIZE_MB}MB total</p>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-3xl mx-auto px-4 py-2">
          {/* Show error alert if there's an API error */}
          {streamingChat.error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {streamingChat.error}
              </AlertDescription>
            </Alert>
          )}
          
          <MessageList 
            messages={session.messages} 
            onFeedback={handleFeedback}
            showSources={!!API_CONFIG.ENABLE_SOURCE_ATTRIBUTION}
          />
          
          {/* Show streaming message only when actively streaming */}
          {streamingChat.isStreaming && (
            <StreamingMessage streamingState={streamingChat} />
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="flex-shrink-0 bg-background">
        <div className="max-w-3xl mx-auto px-4 py-2">
          {/* Image attachments preview */}
          {attachments.length > 0 && (
            <div className="mb-3 p-3 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {attachments.length} image{attachments.length > 1 ? 's' : ''} attached
                </span>
                <span className="text-xs text-gray-500">
                  Max {MAX_FILE_SIZE_MB}MB total
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((attachment, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={attachment.data_url}
                      alt={`Attachment ${index + 1}`}
                      className="w-16 h-16 object-cover rounded border"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeAttachment(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <Textarea
              ref={textareaRef}
              id={`chat-input-${sessionId}`}
              name={`message-${sessionId}`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={attachments.length > 0 ?
                "Geben Sie hier Ihre Anfrage ein..." :
                "Geben Sie hier Ihre Anfrage ein oder ziehen Sie Bilder hierher..."}
              className="min-h-[48px] max-h-[200px] pr-12 text-base resize-none"
              rows={1}
              disabled={streamingChat.isStreaming}
            />
            <Button
              onClick={() => {
                if (streamingChat.isStreaming) {
                  streamingChat.cancelStreaming();
                } else {
                  handleSendMessage();
                }
              }}
              disabled={(!inputValue.trim() && !streamingChat.isStreaming)}
              className={`absolute bottom-2 right-2 h-8 w-8 p-0 rounded-full transition-colors ${
                streamingChat.isStreaming
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700 disabled:bg-muted disabled:text-muted-foreground'
              }`}
              title={streamingChat.isStreaming ? 'Stop' : 'Send'}
            >
              {streamingChat.isStreaming ? (
                <Square className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          {!streamingChat.isStreaming && (
            <div className="text-xs text-muted-foreground mt-2 text-center">
              Der PWA Chatbot stellt lediglich ein Hilfsmittel dar. Die Nutzung entbindet Dich nicht von der Verpflichtung, die Ergebnisse der Software auf Plausibilität, Richtigkeit und Verwendbarkeit für den beabsichtigten Zweck zu überprüfen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSession;